// src/core/lib/internal-api-client.ts — single chokepoint for INTERNAL API calls
// (PRI-496 Phase 0).
//
// "Internal" = first-party web-app endpoints that are NOT part of the published
// public API contract (/v1/openapi.json): the legacy /v1/video/* pipeline,
// /v1/jobs/*, /v1/stream/* (singular — the public plane is /v1/streams),
// /v1/admin/*, /v1/user-videos, /v1/demo-videos, /v1/prompts, /v1/waitlist,
// /v1/invite/*, /v1/feedback, /v1/model-info, /v1/intercom-token,
// /v1/user/profile, billing portal endpoints, etc.
//
// Every internal call the user-facing web app makes MUST go through this
// module. This is the CI-guard chokepoint for the Phase 3 cutover to public
// APIs: once cutover completes, a grep/lint rule can forbid new imports here
// and shrink this file to nothing. Public-contract calls ride the SDK
// transport directly (src/core/lib/api/client.ts / parse.ts / sdk.ts) and do
// NOT belong here.
//
// Pure delegation — zero behavior change:
//   internalRequest → the exact same request() (SDK transport + legacy error
//                     shape) every module already used.
//   internalFetch   → global fetch against API_BASE_URL (or an explicit base
//                     override, preserving the useStreamSession apiBase flow).
//   internalApiUrl  → `${base}${path}` URL builder for <video> tags / XHR.

import { API_BASE_URL } from './api/base'
import { request } from './api/client'

export { API_BASE_URL }

/** Build an absolute URL for an internal API path (URL builders, XHR, <video> src). */
export function internalApiUrl(path: string, base: string = API_BASE_URL): string {
  return `${base}${path}`
}

/**
 * JSON request to an internal endpoint via the shared SDK transport.
 * Identical semantics to request() in ./api/client — auth from the token
 * registry, legacy error shape ({ code, status, upgradeUrl }) preserved.
 */
export function internalRequest<T>(
  path: string,
  options?: RequestInit & { skipContentType?: boolean },
): Promise<T> {
  return request<T>(path, options)
}

/**
 * Raw fetch to an internal endpoint. For call sites that need Response-level
 * control (status probing, timeouts via AbortSignal, non-JSON bodies).
 * `base` overrides API_BASE_URL (streaming pre-connect passes its own apiBase).
 */
export function internalFetch(
  path: string,
  init?: RequestInit & { base?: string },
): Promise<Response> {
  const { base, ...rest } = init ?? {}
  return fetch(internalApiUrl(path, base ?? API_BASE_URL), rest)
}
